import React, { useCallback, useEffect, useState } from 'react';
import api from '../services/api';

interface Config {
  tenant_id: number;
  api_key: string | null;
  is_enabled: boolean;
  poll_interval_minutes: number;
  last_pulled_at: string | null;
  last_success_at: string | null;
  last_error: string | null;
  last_error_at: string | null;
  total_leads_pulled: number;
  total_pulls: number;
  exists: boolean;
}

interface PullResult {
  stats: {
    pulled: number; created: number; duplicates: number; skipped: number;
    ok: boolean; error: string | null;
  };
  config: Config;
}

const fmtDate = (iso: string | null): string =>
  !iso ? '—' : new Date(iso).toLocaleString('en-IN');

const StatusPill: React.FC<{ ok: boolean | null; label: string }> = ({ ok, label }) => (
  <span
    style={{
      display: 'inline-block', fontSize: 11, fontWeight: 700, letterSpacing: 0.3,
      textTransform: 'uppercase', padding: '3px 10px', borderRadius: 20,
      background: ok === null ? '#e2e8f0' : ok ? '#dcfce7' : '#fee2e2',
      color:      ok === null ? '#64748b' : ok ? '#166534' : '#991b1b',
    }}
  >
    {label}
  </span>
);

const IndiaMartIntegrationPage: React.FC = () => {
  const [cfg, setCfg]           = useState<Config | null>(null);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [pulling, setPulling]   = useState(false);
  const [error, setError]       = useState<string | null>(null);
  const [successMsg, setSuccess] = useState<string | null>(null);
  const [reveal, setReveal]     = useState(false);

  // Editable draft — separate from server config so saves are explicit
  const [draftKey, setDraftKey]         = useState<string>('');
  const [draftEnabled, setDraftEnabled] = useState<boolean>(true);
  const [draftInterval, setDraftInterval] = useState<number>(5);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const { data } = await api.get<Config>('/admin/indiamart-pull-config');
      setCfg(data);
      setDraftKey(data.api_key ?? '');
      setDraftEnabled(data.is_enabled);
      setDraftInterval(data.poll_interval_minutes);
    } catch (e: any) {
      setError(e?.response?.data?.error ?? 'Failed to load config');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true); setError(null); setSuccess(null);
    try {
      const { data } = await api.put<Config>('/admin/indiamart-pull-config', {
        api_key: draftKey || undefined,
        is_enabled: draftEnabled,
        poll_interval_minutes: draftInterval,
      });
      setCfg(data);
      setDraftKey(data.api_key ?? '');
      setSuccess('Saved');
      setTimeout(() => setSuccess(null), 3000);
    } catch (e: any) {
      setError(e?.response?.data?.error ?? 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const pullNow = async () => {
    setPulling(true); setError(null); setSuccess(null);
    try {
      const { data } = await api.post<PullResult>('/admin/indiamart-pull-config/pull-now');
      setCfg(data.config);
      if (data.stats.ok) {
        setSuccess(`Pulled ${data.stats.pulled} · created ${data.stats.created} · duplicates ${data.stats.duplicates} · skipped ${data.stats.skipped}`);
      } else {
        setError(`Pull failed: ${data.stats.error ?? 'unknown'}`);
      }
    } catch (e: any) {
      setError(e?.response?.data?.error ?? 'Failed to trigger pull');
    } finally {
      setPulling(false);
    }
  };

  if (loading) return <div className="spinner">Loading…</div>;
  if (!cfg) return null;

  // Overall integration state — green when enabled + recent success, amber when
  // enabled but no recent success, red when there's a recent error, grey when
  // disabled or not configured.
  let overallOk: boolean | null = null;
  let overallLabel = 'Not configured';
  if (cfg.exists && cfg.api_key) {
    if (!cfg.is_enabled) {
      overallLabel = 'Disabled';
    } else if (cfg.last_error && cfg.last_error_at && (!cfg.last_success_at || cfg.last_error_at > cfg.last_success_at)) {
      overallOk = false; overallLabel = 'Error';
    } else if (cfg.last_success_at) {
      overallOk = true; overallLabel = 'Healthy';
    } else {
      overallLabel = 'Awaiting first pull';
    }
  }

  return (
    <div className="dashboard-page">
      <div className="page-hd" style={{ marginBottom: 20 }}>
        <div className="page-hd-left">
          <h1 className="page-title">IndiaMART Integration</h1>
          <p className="page-meta">
            Pull API — polls IndiaMART on a schedule and imports new buy-leads into the CRM
          </p>
        </div>
        <div className="page-hd-right">
          <StatusPill ok={overallOk} label={overallLabel} />
        </div>
      </div>

      {error   && <div className="alert alert-error"   style={{ marginBottom: 12 }}>{error}</div>}
      {successMsg && <div className="alert alert-success" style={{ marginBottom: 12 }}>{successMsg}</div>}

      <div className="charts-grid" style={{ gridTemplateColumns: '2fr 1fr', gap: 20 }}>
        {/* Left: config form */}
        <div className="chart-card">
          <div className="chart-title">Configuration</div>

          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#334155', margin: '16px 0 6px' }}>
            API Key <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>· from IndiaMART Lead Manager → Pull API</span>
          </label>
          <div style={{ display: 'flex', gap: 6 }}>
            <input
              type={reveal ? 'text' : 'password'}
              value={draftKey}
              onChange={e => setDraftKey(e.target.value)}
              placeholder="mRyv9E5o4Xz9SPHt4W7…"
              style={{
                flex: 1, padding: '10px 12px', fontSize: 14,
                border: '1px solid #cbd5e1', borderRadius: 8, background: '#fff',
                fontFamily: 'monospace',
              }}
            />
            <button
              onClick={() => setReveal(r => !r)}
              className="btn btn-ghost"
              style={{ padding: '10px 14px' }}
            >
              {reveal ? 'Hide' : 'Reveal'}
            </button>
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
            Key expires after 7 consecutive days without a successful API call. Not a concern when the scheduler is active (polls every {cfg.poll_interval_minutes} min).
          </div>

          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#334155', margin: '20px 0 6px' }}>
            Poll interval (minutes) <span style={{ color: 'var(--text-muted)', fontWeight: 400 }}>· min 5, max 1440</span>
          </label>
          <input
            type="number"
            min={5}
            max={1440}
            value={draftInterval}
            onChange={e => setDraftInterval(parseInt(e.target.value || '5', 10))}
            style={{
              width: 120, padding: '10px 12px', fontSize: 14,
              border: '1px solid #cbd5e1', borderRadius: 8, background: '#fff',
            }}
          />

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 20, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={draftEnabled}
              onChange={e => setDraftEnabled(e.target.checked)}
              style={{ width: 18, height: 18 }}
            />
            <span style={{ fontSize: 14, fontWeight: 600, color: '#334155' }}>
              Enable automatic polling
            </span>
          </label>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 26 }}>
            When enabled, the scheduler pulls new IndiaMART leads on the interval above.
          </div>

          <div style={{ display: 'flex', gap: 10, marginTop: 24 }}>
            <button
              className="btn btn-primary"
              onClick={save}
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Save configuration'}
            </button>
            <button
              className="btn btn-ghost"
              onClick={pullNow}
              disabled={pulling || !draftKey}
              title={!draftKey ? 'Set an API key first' : 'Manually trigger a pull now'}
            >
              {pulling ? 'Pulling…' : 'Pull now'}
            </button>
          </div>
        </div>

        {/* Right: status card */}
        <div className="chart-card">
          <div className="chart-title">Status</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 12 }}>
            <StatusRow label="Last pull attempt"   value={fmtDate(cfg.last_pulled_at)} />
            <StatusRow label="Last successful pull" value={fmtDate(cfg.last_success_at)} />
            <StatusRow label="Total pulls"         value={String(cfg.total_pulls)} />
            <StatusRow label="Total leads pulled"  value={String(cfg.total_leads_pulled)} />
          </div>

          {cfg.last_error && (
            <div style={{
              marginTop: 16, padding: 12, background: '#fef2f2', borderRadius: 8,
              border: '1px solid #fecaca',
            }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#991b1b', textTransform: 'uppercase', letterSpacing: 0.3 }}>
                Last error · {fmtDate(cfg.last_error_at)}
              </div>
              <div style={{ fontSize: 12, color: '#7f1d1d', marginTop: 4, fontFamily: 'monospace' }}>
                {cfg.last_error}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const StatusRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
    <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{label}</span>
    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--navy)' }}>{value}</span>
  </div>
);

export default IndiaMartIntegrationPage;
